import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { supabase } from '@/lib/supabase';
import { prepareNEMSISExport } from '@/lib/nemsis-service';
import { playSubmitChime } from '@/lib/sound-effects';
import type { EPCR, TransmissionStatus } from '@/types';

/**
 * Automatic RIEMSIS transmission with visible status + retry.
 *
 * State machine for epcrs.transmission_status:
 *   not_submitted → queued → transmitting → (sent | failed)
 *   failed → retry_pending → transmitting (repeat)
 *   sent → confirmed (once RIEMSIS ack received — future)
 */

const RETRY_DELAYS_MS = [30_000, 2 * 60_000, 5 * 60_000, 15 * 60_000, 60 * 60_000];
// Attempt 1 after 30s, then 2m, 5m, 15m, 60m. After 5 attempts → manual intervention.

/**
 * Called when an ePCR is locked (QA sign-off).
 * Queues the transmission and starts the first attempt.
 */
export async function queueTransmission(epcrId: string): Promise<void> {
  await updateStatus(epcrId, {
    transmission_status: 'queued',
    transmission_attempts: 0,
    transmission_last_error: null,
  });

  // Kick off first attempt asynchronously
  attemptTransmission(epcrId).catch((err) => {
    console.error('[transmission] initial attempt failed:', err);
  });
}

/**
 * Try to transmit one ePCR. Updates status based on result.
 * Safe to call repeatedly — idempotent via status checks.
 */
export async function attemptTransmission(epcrId: string): Promise<{ ok: boolean; error?: string }> {
  const epcr = await db.epcrs.get(epcrId);
  if (!epcr) return { ok: false, error: 'ePCR not found' };
  if (epcr.transmission_status === 'sent' || epcr.transmission_status === 'confirmed') {
    return { ok: true };
  }
  if (epcr.transmission_status === 'transmitting') {
    return { ok: false, error: 'Already in flight' };
  }

  await updateStatus(epcrId, {
    transmission_status: 'transmitting',
    transmission_submitted_at: new Date().toISOString(),
  });

  try {
    // 1. Generate + validate XML locally
    const { xml, validation } = await prepareNEMSISExport(epcrId);
    if (!validation.valid) {
      const msg = `XML validation failed: ${validation.errors.map(e => e.message).join('; ')}`;
      await handleFailure(epcrId, msg);
      return { ok: false, error: msg };
    }

    // 2. Post via edge function (server-side has credentials)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      // Dev mode — simulate success
      await markSent(epcrId);
      return { ok: true };
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      await handleFailure(epcrId, 'Not authenticated');
      return { ok: false, error: 'Not authenticated' };
    }

    const resp = await fetch(`${supabaseUrl}/functions/v1/riemsis-post`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ epcr_id: epcrId, xml }),
    });

    if (resp.ok) {
      await markSent(epcrId);
      return { ok: true };
    }
    const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
    await handleFailure(epcrId, err.error ?? `HTTP ${resp.status}`);
    return { ok: false, error: err.error };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    await handleFailure(epcrId, msg);
    return { ok: false, error: msg };
  }
}

async function markSent(epcrId: string) {
  await updateStatus(epcrId, {
    transmission_status: 'sent',
    transmission_confirmed_at: new Date().toISOString(),
    transmission_last_error: null,
  });
  // Satisfying confirmation chime — Resend-style two-note ascending
  // ding so the EMT (and the chief watching over their shoulder)
  // hears that the post landed cleanly. No-op when sounds are muted
  // or the OS prefers reduced motion.
  void playSubmitChime();
}

async function handleFailure(epcrId: string, errorMessage: string) {
  const epcr = await db.epcrs.get(epcrId);
  const attempts = (epcr?.transmission_attempts ?? 0) + 1;
  const maxReached = attempts >= RETRY_DELAYS_MS.length;

  await updateStatus(epcrId, {
    transmission_status: maxReached ? 'failed' : 'retry_pending',
    transmission_attempts: attempts,
    transmission_last_error: errorMessage,
  });

  // Schedule next retry if not maxed out
  if (!maxReached) {
    const delay = RETRY_DELAYS_MS[attempts - 1];
    setTimeout(() => {
      attemptTransmission(epcrId).catch(() => { /* ignored — handled internally */ });
    }, delay);
  }
}

async function updateStatus(
  epcrId: string,
  updates: {
    transmission_status?: TransmissionStatus;
    transmission_attempts?: number;
    transmission_last_error?: string | null;
    transmission_submitted_at?: string;
    transmission_confirmed_at?: string;
  },
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await db.epcrs.update(epcrId, { ...updates, _dirty: true, _last_modified: new Date().toISOString() } as any);
  await enqueueSync('epcrs', epcrId, 'update', { id: epcrId, ...updates });
}

/**
 * Manual retry — callable from UI after a failure.
 */
export async function retryTransmission(epcrId: string): Promise<void> {
  await updateStatus(epcrId, {
    transmission_status: 'queued',
    transmission_last_error: null,
  });
  await attemptTransmission(epcrId);
}

/**
 * Human-readable label for transmission status.
 */
export function transmissionStatusLabel(status: TransmissionStatus | null | undefined): string {
  switch (status) {
    case 'not_submitted': return 'Not submitted';
    case 'queued': return 'Queued';
    case 'transmitting': return 'Transmitting...';
    case 'sent': return 'Sent to RIEMSIS';
    case 'confirmed': return 'Confirmed by RIEMSIS';
    case 'failed': return 'Failed — manual retry required';
    case 'retry_pending': return 'Retry pending';
    default: return 'Unknown';
  }
}

/**
 * Is the transmission in a terminal state?
 */
export function isTerminalStatus(status: TransmissionStatus | null | undefined): boolean {
  return status === 'sent' || status === 'confirmed' || status === 'failed';
}

/**
 * On app boot, kick retries for any ePCRs stuck in retry_pending.
 * Call from main.tsx once.
 */
export async function resumePendingTransmissions(): Promise<void> {
  const pending = await db.epcrs
    .where('transmission_status')
    .anyOf(['queued', 'retry_pending'])
    .toArray()
    .catch(() => [] as EPCR[]);

  for (const epcr of pending) {
    attemptTransmission(epcr.id).catch(() => { /* non-fatal */ });
  }
}
